import React, {
    FC,
    Fragment,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { CxiLicenceTypes, SelfProvisioning, UsersByExtension as UsersByExtensionLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { DevicesResponse, LicenceResponseMapped, SessionAPIResponse } from '../../../NonReact/Types/Types'
import { COMMON_DEVICES_PARTITION_NAME, EMPTY_STRING } from '../../../NonReact/constants/Common'
import { EPT_DEVICE_FIELDS } from '../../../NonReact/constants/DeviceConstants'
import { DEVICE_FILTER_EXTENSIONS_ONLY, DeviceTypeIdString } from '../../../NonReact/constants/DeviceTypes'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { StatusCodes } from '../../../NonReact/constants/StatusCodes'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { FormProvider, useForm, useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { handleUnknownForAvailables } from '../../AddUser/AddUserFunctions'
import { DEFAULT_CME_ID } from '../../AddUser/Constants'
import { RESTAPIError } from '../../Common/API'
import { getSession } from '../../Common/API/CommonAPI'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { getCapabilitiesFromCME, getCMEIDFromTelSys } from '../../Common/API/ItemGetters'
import {
    ALL_VALUE,
    countLicencedUsersForTelSysPartition,
    getDevicesFromPartition,
    getPartitionsForTelServer,
    getTelephonyServer,
    getTelServersAsSelectOptions
} from '../../Common/API/ItemsGetters'
import { getTelSysLicences } from '../../Common/API/Licence/LicenceAPI'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'
import { PaginatedList } from '../../Common/Components/PaginatedList/PaginatedList'
import { DeviceSelectItem, SelectOption, SelectOptions } from '../../Common/Components/Selects/Types'
import { ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { CommonFormContext, CommonFormContextProvider, FormContextType } from '../../Common/Contexts/CommonFormContext'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import UserInfoContext, { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { useStateAsync } from '../../Common/Hooks/useStateAsync'
import { UserDefaults } from '../../Common/Types'
import { $error } from '../../Common/Utils/ConsoleUtils'
import { displayErrorMessage, displayInfoMessage, displaySuccessMessage } from '../../Common/Utils/Messages'
import {
    PARTITION_FILTER_NAME,
    SECURITY_GROUP_FILTER_NAME,
    TELSYS_FILTER_NAME
} from '../../InsightsHub/Constants/FieldNames'
import { UserTypes } from '../../SelfProvisioning/Constants/Constants'
import { userFormStateMapper } from '../../SelfProvisioning/Mappers/UserMapper'
import userDefaults from '../Config/UserDefaults.json'
import { UserByExtensionEntry } from '../Entries/UserByExtensionEntry'
import { AddAllUsers } from '../Header/AddAllUsers'
import { LicenceSummary } from '../Header/LicenceSummary'
import { UsersByExtensionForm } from '../Types/Types'
import {
    ADVANCED_FIELDS,
    AUTH_USER_MISSING_ROLES,
    HISTORIC_ONLY,
    INTEGRATION_MODE,
    INTEGRATION_TYPE
} from '../../../NonReact/constants/TelSysConstants'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import { AdminBackendPermissionNames } from '../../Common/Constants/Permissions'
import { useFeatureFlag } from '../../Common/Hooks/useFeatureFlag'
import { CXI_FEATURE_FLAG_KEY_FOR_TELSYS, CX_SCORE_FEATURE_FLAG } from '../../Common/Constants/FeatureFlags'
import { ProvisionAllCxi } from '../Header/ProvisionAllCxi'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { IconName } from '../../Common/Components/Icon'
import { getWelcomeEmailFieldName } from '../Constants/FieldNames'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { isBitmaskMatching } from '../../Common/Utils/BitwiseOperations'
import { isCxsProductApplicable, parseBusinessIntegrationOptions } from '../../Common/Utils/CxProductApplicability'
import { telSysCapabilityBitFlags } from '../../CMEAddModify/Constants'
import { AdminUserBody, ApplicationUserResponse, JSONResponse } from '../../../NonReact/common/Types/Types'
import { createUser, getUser, updateUser } from '../../../NonReact/common/API/EntityAPIClient'
import {
    CxIntegrationLicence,
    SelfProvisioningUser
} from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import {
    IndividualTelItem,
    MessageResponseTuple,
    ReactState,
    ReactStateAsync,
    TelSysIntegrationMode
} from '../../Common/Types/Types'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { StatusMessage } from '../../Common/Components/StatusMessage/StatusMessage'
import { ALL_VALUE_STRING_DEFAULT } from '../../../NonReact/common/DefaultValues'
import { getCxiEntitlementVisibility } from '../../Common/Utils/CxiEntitlementRules'

export const UsersByExtension: FC = (): ReactElement => {
    const methods: UseFormMethods<UsersByExtensionForm> = useForm({
        mode: ON_CHANGE,
        shouldUnregister: false
    })

    const { handleSubmit }: UseFormMethods<UsersByExtensionForm> = methods

    return (
        <FormProvider {...methods}>
            <CommonFormContextProvider>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit((): void => {
                        // Do nothing on purpose
                    })}
                >
                    <UsersByExtensionContent />
                </form>
            </CommonFormContextProvider>
        </FormProvider>
    )
}

export const UsersByExtensionContent: FC = (): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(UsersByExtensionLanguage.NAME)
    language.loadBundle(SelfProvisioning.NAME)
    language.loadBundle(CxiLicenceTypes.NAME)

    const userInfo: UserInfoContextType = useContext(UserInfoContext)
    const { displayError }: MessageContextType = useContext(MessageContext)
    const formContext: Nullable<FormContextType> = useContext(CommonFormContext)

    const { reset, watch, setValue, getValues }: UseFormMethods<UsersByExtensionForm> = useFormContext()

    const [cmeID, setCmeID]: ReactState<string> = useState<string>(DEFAULT_CME_ID)
    const [capabilities, setCapabilities]: ReactState<Definable<number>> = useState<Definable<number>>()
    const [availableExtensions, setAvailableExtensions]: ReactState<DeviceSelectItem[]> = useState<DeviceSelectItem[]>(
        []
    )
    const [totalExtensionCount, setTotalExtensionCount]: ReactState<Definable<number>> = useState<Definable<number>>()
    const [extensionErrors, setExtensionErrors, getExtensionErrors]: ReactStateAsync<Definable<string>[]> =
        useStateAsync<Definable<string>[]>([])

    const [availableTelServers, setAvailableTelServers]: ReactState<Definable<SelectOptions<number>>> =
        useState<SelectOptions<number>>()
    const [availablePartitions, setAvailablePartitions]: ReactState<Definable<SelectOptions<number>>> =
        useState<SelectOptions<number>>()
    const [availableSecurityGroups, setAvailableSecurityGroups]: ReactState<Definable<SelectOptions<string>>> =
        useState<SelectOptions<string>>()

    const [currentTelSys, setCurrentTelSys]: ReactState<Nullable<IndividualTelItem>> =
        useState<Nullable<IndividualTelItem>>()

    const [licenceSummary, setLicenceSummary]: ReactStateAsync<Nullable<LicenceResponseMapped>> =
        useStateAsync<Nullable<LicenceResponseMapped>>(undefined)

    const [
        isPartitionChangeRequired,
        setIsPartitionChangeRequired,
        getIsPartitionChangeRequired
    ]: ReactStateAsync<boolean> = useStateAsync<boolean>(false)

    const [isLoadingTelServers, setIsLoadingTelServers]: ReactState<boolean> = useState<boolean>(false)
    const [isLoadingPartitions, setIsLoadingPartitions]: ReactState<boolean> = useState<boolean>(false)

    const [loadingText, setLoadingText]: ReactState<Nullable<string>> = useState<Nullable<string>>()

    const [currentSearchTerm, setCurrentSearchTerm]: ReactState<Nullable<string>> = useState<Nullable<string>>()

    const [currentUserCount, setCurrentUserCount]: ReactState<Definable<number>> = useState<Definable<number>>()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const hasLoaded: MutableRefObject<boolean> = useRef(false)

    const doesCurrentUserHaveUserWriteAccess: boolean = useMemo(
        (): boolean =>
            PermissionsUtil.hasPermissions(
                userInfo,
                PermissionsUtil.builder(AdminBackendPermissionNames.USER).create().update()
            ),
        [userInfo]
    )

    const isAdminProvisioningUser: boolean = useMemo(
        (): boolean => doesCurrentUserHaveUserWriteAccess && currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS,
        [doesCurrentUserHaveUserWriteAccess, currentTelSys]
    )

    const cxIntegrationLicenceType: CxIntegrationLicence = useMemo(
        (): CxIntegrationLicence =>
            currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS ||
            currentTelSys?.TelSysType === TelSysTypes.WEBEX_CALLING
                ? CxIntegrationLicence.CXI
                : CxIntegrationLicence.SOLO,
        [currentTelSys]
    )

    const telSysValue: number = watch(
        TELSYS_FILTER_NAME,
        availableTelServers ? availableTelServers[0]?.value : ALL_VALUE
    )
    const partitionValue: number = watch(PARTITION_FILTER_NAME)
    const secGroupValue: Definable<string> = watch(SECURITY_GROUP_FILTER_NAME)
    const secGroupValueRef: MutableRefObject<Definable<string>> = useRef<Definable<string>>(secGroupValue)

    const isCxiOnly: boolean = currentTelSys?.IntegrationMode === TelSysIntegrationMode.CXI_ONLY

    const updateDeviceUsers = (index: number, newUsersValue: string[]): void => {
        const newAvailableExtensions: DeviceSelectItem[] = [...availableExtensions]

        if (newAvailableExtensions.length > index && !!newAvailableExtensions?.[index]?.original?.Users) {
            const deviceResponse: DevicesResponse = newAvailableExtensions[index].original as DevicesResponse

            deviceResponse.updateNumber = (deviceResponse.updateNumber ?? 0) + 1
            deviceResponse.Users = newUsersValue
        }

        if (isMounted.current) {
            setAvailableExtensions(newAvailableExtensions)
        }
    }

    const updateExtensionErrors = useCallback(
        (index: number, newError?: string): void => {
            getExtensionErrors().then((errors: Definable<string>[]): void => {
                const newExtensionErrors: Definable<string>[] = [...errors]

                if (newExtensionErrors.length > index) {
                    newExtensionErrors[index] = newError
                }

                if (isMounted.current) {
                    setExtensionErrors(newExtensionErrors)
                }
            })
        },
        [extensionErrors]
    )

    const updateLicenceSummary = useCallback((): void => {
        if (currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS && currentTelSys?.TelSysID !== ALL_VALUE) {
            getTelSysLicences(currentTelSys?.TelSysID)
                .then((telSysLicences: Nullable<LicenceResponseMapped>): Nullable<LicenceResponseMapped> => {
                    setLicenceSummary(telSysLicences)

                    return telSysLicences
                })
                .catch($error)
        }
    }, [currentTelSys])

    const addUser = (
        { original, isAgent, index }: DeviceSelectItem,
        userType: string,
        refetchedCME: string,
        cxiLicence: CxIntegrationLicence = CxIntegrationLicence.NONE,
        sendWelcomeEmail: boolean = true
    ): Promise<boolean> => {
        if ((original?.Users?.length ?? 0) === 0) {
            return getSession()
                .then(async (session: JSONResponse<SessionAPIResponse>): Promise<boolean> => {
                    if (isMounted.current && session?.SessionID) {
                        const idFromTelSys: string = original?.TelSysUsername ?? EMPTY_STRING

                        const user: AdminUserBody = await userFormStateMapper(
                            userDefaults as unknown as UserDefaults,
                            {
                                email: original?.TelSysUsername ?? EMPTY_STRING,
                                name: original?.Description ?? EMPTY_STRING,
                                userType,
                                cxiLicence,
                                welcomeEmail: isCxiOnly ? false : sendWelcomeEmail
                            },
                            refetchedCME,
                            watch(TELSYS_FILTER_NAME, ALL_VALUE),
                            watch(PARTITION_FILTER_NAME, ALL_VALUE),
                            session?.Nonce,
                            original,
                            isAgent,
                            undefined,
                            idFromTelSys
                        )

                        return createUser(user)
                            .then((): boolean => {
                                if (isMounted.current) {
                                    updateDeviceUsers(index ?? 0, [...(original?.Users ?? []), user.Username])
                                    updateExtensionErrors(index ?? 0, undefined)
                                }

                                return true
                            })
                            .catch((error: RESTAPIError): boolean => {
                                if (isMounted.current) {
                                    updateExtensionErrors(index ?? 0, error.message)

                                    if (error.HTTPStatus === StatusCodes.OK) {
                                        updateDeviceUsers(index ?? 0, [...(original?.Users ?? []), user.Username])

                                        return true
                                    }
                                }

                                return false
                            })
                    }

                    return false
                })
                .catch((e: Error): boolean => {
                    displayError(e)
                    return false
                })
        }

        return Promise.reject()
    }

    const devicesWithoutUsers: DeviceSelectItem[] = useMemo((): DeviceSelectItem[] => {
        if (!availableExtensions) return []
        return availableExtensions.filter(
            (ext: DeviceSelectItem): boolean => (ext?.original?.Users ?? [])?.length === 0
        )
    }, [availableExtensions])

    const toggleWelcomeAll = (): void => {
        const toggleValue: boolean = getValues()?.users?.some(
            (user: SelfProvisioningUser) => user.welcomeEmail === false
        )
        for (let index = 0; index < availableExtensions.length; index++) {
            setValue(getWelcomeEmailFieldName(index), toggleValue)
        }
    }

    const addAllUsers = useCallback(
        async (userType: string, shouldSendWelcomeEmail: boolean): Promise<void> => {
            if (availableExtensions && availableExtensions.length > 0) {
                setLoadingText(
                    language.getInterpolated(
                        UsersByExtensionLanguage.AddAllUsers_CreatingUsers,
                        devicesWithoutUsers.length,
                        language.getWithTelSysCheck(`UsersByExtension_UserType_${userType}`, currentTelSys?.TelSysType)
                    )
                )

                let availableLicencesForUserType: number = Number.MAX_SAFE_INTEGER

                if (userType === UserTypes.PROFESSIONAL) {
                    availableLicencesForUserType =
                        licenceSummary?.AvailableLicences?.Professional ?? Number.MAX_SAFE_INTEGER
                } else if (userType === UserTypes.ESSENTIALS) {
                    availableLicencesForUserType =
                        licenceSummary?.AvailableLicences?.Essentials ?? Number.MAX_SAFE_INTEGER
                } else if (userType === UserTypes.CALL_CENTRE_AGENT) {
                    availableLicencesForUserType =
                        licenceSummary?.AvailableLicences?.CallCentreAgent ?? Number.MAX_SAFE_INTEGER
                }

                if (devicesWithoutUsers.length <= 0) {
                    displayInfoMessage(language.get(UsersByExtensionLanguage.NoUsersAdded))
                    setLoadingText(undefined)

                    return undefined
                }

                if (devicesWithoutUsers.length > availableLicencesForUserType && availableLicencesForUserType >= 0) {
                    let tooManyUsersMessage: string

                    if (availableLicencesForUserType <= 0) {
                        tooManyUsersMessage = language.getInterpolated(
                            devicesWithoutUsers.length > 1
                                ? UsersByExtensionLanguage.TooManyUsers_NoLicences_Plural
                                : UsersByExtensionLanguage.TooManyUsers_NoLicences,
                            devicesWithoutUsers.length
                        )
                    } else if (availableLicencesForUserType === 1) {
                        tooManyUsersMessage = language.getInterpolated(
                            devicesWithoutUsers.length > 1
                                ? UsersByExtensionLanguage.TooManyUsers_UsersPlural_LicenceNonPlural
                                : UsersByExtensionLanguage.TooManyUsers_UsersNonPlural_LicenceNonPlural,
                            devicesWithoutUsers.length,
                            availableLicencesForUserType
                        )
                    } else {
                        tooManyUsersMessage = language.getInterpolated(
                            devicesWithoutUsers.length > 1
                                ? UsersByExtensionLanguage.TooManyUsers_Plural
                                : UsersByExtensionLanguage.TooManyUsers,
                            devicesWithoutUsers.length,
                            availableLicencesForUserType
                        )
                    }

                    displayErrorMessage(tooManyUsersMessage)
                    setLoadingText(undefined)

                    return undefined
                }

                const totalNumberOfUsers: number = Object.values(licenceSummary?.UsedLicences ?? {}).reduce<number>(
                    (total: number, value: Nullable<number>): number => total + (value ?? 0),
                    0
                )

                if (
                    devicesWithoutUsers.length + totalNumberOfUsers >
                    (currentTelSys?.Advanced?.MaximumAllowedUsersMonitored ?? Number.MAX_SAFE_INTEGER)
                ) {
                    displayErrorMessage(
                        language.getInterpolated(
                            devicesWithoutUsers.length > 1
                                ? UsersByExtensionLanguage.TooManyUsers_MaxTelSysCapReached_Plural
                                : UsersByExtensionLanguage.TooManyUsers_MaxTelSysCapReached,
                            devicesWithoutUsers.length
                        )
                    )
                    setLoadingText(undefined)

                    return undefined
                }

                const refetchedCMEID: string = await getCMEIDFromTelSys(telSysValue)
                    .then((id: Definable<string>): string => (isVarNotEmpty(id) ? id : DEFAULT_CME_ID))
                    .catch((): string => DEFAULT_CME_ID)

                Promise.all(
                    devicesWithoutUsers
                        .filter((device: DeviceSelectItem): boolean =>
                            userType === UserTypes.CALL_CENTRE_AGENT ? device?.isAgent ?? true : true
                        )
                        .map(
                            (device: DeviceSelectItem): Promise<boolean> =>
                                addUser(
                                    device,
                                    userType,
                                    refetchedCMEID,
                                    CxIntegrationLicence.NONE,
                                    shouldSendWelcomeEmail
                                )
                        )
                )
                    .then((results: boolean[]): void => {
                        if (isMounted.current) {
                            const successfullyAddedUsers: number =
                                results?.filter((res: boolean): boolean => res)?.length ?? 0

                            if (successfullyAddedUsers > 0) {
                                displaySuccessMessage(
                                    language.getInterpolated(
                                        UsersByExtensionLanguage.SuccessfullyAddedUsers,
                                        successfullyAddedUsers,
                                        language.getWithTelSysCheck(
                                            `UsersByExtension_UserType_${userType}`,
                                            currentTelSys?.TelSysType
                                        )
                                    )
                                )
                            } else {
                                displayInfoMessage(language.get(UsersByExtensionLanguage.NoUsersAdded))
                            }
                        }
                    })
                    .catch(displayError)
                    .finally((): void => {
                        setLoadingText(undefined)
                        updateLicenceSummary()
                    })
            }

            return undefined
        },
        [availableExtensions, licenceSummary, currentTelSys]
    )

    const isCXIFeatureEnabled: boolean =
        useFeatureFlag(CXI_FEATURE_FLAG_KEY_FOR_TELSYS[currentTelSys?.TelSysType as TelSysTypes], false) ?? false

    const isCXICapabilityEnabled: boolean =
        isVarNotEmpty(currentTelSys?.TelSysType) &&
        isVarNotEmpty(capabilities) &&
        isBitmaskMatching(capabilities, telSysCapabilityBitFlags[currentTelSys.TelSysType])

    const isCxiEnabled: boolean = isCXIFeatureEnabled && isCXICapabilityEnabled
    const cxiEntitlementVisibility = useMemo(
        () => getCxiEntitlementVisibility(currentTelSys?.TelSysType, isCxiEnabled),
        [currentTelSys?.TelSysType, isCxiEnabled]
    )

    const isTeams: boolean = currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS

    const isCXScoreFeatureEnabled: boolean = useFeatureFlag(CX_SCORE_FEATURE_FLAG, false)

    // CXS column follows the shared product rule (CxProductApplicability): MS Teams requires the
    // CX-Score business option; BroadWorks (or a Simulator emulating it) requires an Enterprise
    // partition licence; any other telsys (e.g. Webex Calling) is never eligible.
    const showCxsSubscriptionColumn: boolean =
        isCXScoreFeatureEnabled &&
        isCxsProductApplicable(
            currentTelSys?.TelSysType,
            parseBusinessIntegrationOptions(currentTelSys?.IntegrationModeOptions),
            isStringNotEmpty(currentTelSys?.LicensedForRptUserType)
                ? [currentTelSys?.LicensedForRptUserType]
                : undefined,
            currentTelSys?.CallControlOptions
        )

    const shouldShowReportingTypeColumn = (): boolean => {
        if (cxiEntitlementVisibility.isApiOnlyMode) return false
        if (!isCxiEnabled || IsVarEmpty(currentTelSys?.IntegrationMode)) return true
        return !isCxiOnly
    }

    const isReportingTypeColumnVisible: boolean = shouldShowReportingTypeColumn()

    const assignUsersCXILicense = ({ original, index }: DeviceSelectItem): Promise<boolean> => {
        const username: string = original?.Users?.[0] as string
        return getUser(username)
            .then((user: JSONResponse<ApplicationUserResponse>) => {
                if (user?.Data.Scope.CXILicenseType.toUpperCase() === cxIntegrationLicenceType) {
                    return false
                }

                return updateUser(username, {
                    Scope: { CXILicenseType: cxIntegrationLicenceType }
                } as AdminUserBody)
                    .then((): boolean => {
                        const idx: number = index ?? 0

                        if (isMounted.current) {
                            updateDeviceUsers(idx, [...(original?.Users ?? [])])
                            updateExtensionErrors(idx)
                        }

                        return true
                    })
                    .catch((error: RESTAPIError): boolean => {
                        if (isMounted.current) {
                            updateExtensionErrors(index ?? 0, error.message)

                            if (error.HTTPStatus === StatusCodes.OK) {
                                updateDeviceUsers(index ?? 0, [...(original?.Users ?? []), username])

                                return true
                            }
                        }

                        return false
                    })
            })
            .catch((error: RESTAPIError): boolean => {
                if (isMounted.current) {
                    updateExtensionErrors(index ?? 0, error.message)
                }
                return false
            })
    }

    const provisionAllUnlicensedDevicesWithCXIntegration: () => Promise<void> = useCallback(async (): Promise<void> => {
        const existingUsers: DeviceSelectItem[] = availableExtensions.filter(
            (ext: DeviceSelectItem): boolean => (ext?.original?.Users ?? [])?.length > 0
        )

        if (ArrayUtils.isEmpty(devicesWithoutUsers) && ArrayUtils.isEmpty(existingUsers)) {
            displayInfoMessage(language.get(UsersByExtensionLanguage.NoUsersAdded))
            return
        }

        setLoadingText(
            language.getInterpolated(
                UsersByExtensionLanguage.AddAllUsers_CreatingUsers,
                devicesWithoutUsers.length + existingUsers.length,
                language.getWithTelSysCheck(`CrmLicenceType_${cxIntegrationLicenceType}`, currentTelSys?.TelSysType)
            )
        )

        const refetchedCMEID: string = await getCMEIDFromTelSys(telSysValue)
            .then((id: Definable<string>): string => (isVarNotEmpty(id) ? id : DEFAULT_CME_ID))
            .catch((): string => DEFAULT_CME_ID)

        const requests: Promise<boolean>[] = [
            ...devicesWithoutUsers.map(
                (device: DeviceSelectItem): Promise<boolean> =>
                    addUser(device, UserTypes.NONE, refetchedCMEID, cxIntegrationLicenceType, false)
            ),
            ...existingUsers.map((device: DeviceSelectItem): Promise<boolean> => assignUsersCXILicense(device))
        ]

        Promise.all(requests)
            .then((results: boolean[]): void => {
                const successCount: number = results.filter((success: boolean): boolean => success === true)?.length
                if (successCount === 0) {
                    displayInfoMessage(language.get(UsersByExtensionLanguage.NoUsersAdded))
                    return
                }

                displaySuccessMessage(
                    language.getInterpolated(
                        UsersByExtensionLanguage.SuccessfullyAddedUsers,
                        successCount,
                        language.getWithTelSysCheck(
                            `CrmLicenceType_${cxIntegrationLicenceType}`,
                            currentTelSys?.TelSysType
                        )
                    )
                )
            })
            .catch((err): void => displayError(err as Error))
            .finally((): void => {
                if (isMounted.current) {
                    setLoadingText(undefined)
                }
            })
    }, [devicesWithoutUsers])

    const getDevices = (
        telsys: Nullable<IndividualTelItem>,
        partition: number,
        partitionChangeReq: boolean,
        currentOffset: Definable<number>,
        searchTerm: Nullable<string>
    ): void => {
        if (telsys?.TelSysID && partition && !partitionChangeReq) {
            setLoadingText(language.get(UsersByExtensionLanguage.AddAllUsers_Loading))

            getDevicesFromPartition(
                telsys?.TelSysID,
                partition,
                false,
                DEVICE_FILTER_EXTENSIONS_ONLY,
                secGroupValueRef.current !== ALL_VALUE_STRING_DEFAULT ? secGroupValueRef.current : undefined,
                10,
                currentOffset,
                searchTerm ?? undefined,
                EPT_DEVICE_FIELDS
            )
                .then(([devices, total]: [DeviceSelectItem[], Definable<number>]): void => {
                    setAvailableExtensions(devices)
                    setExtensionErrors(devices.map((): undefined => undefined))
                    setLoadingText(undefined)
                    setTotalExtensionCount(total)

                    if (isMounted.current) {
                        reset({
                            telsys: telsys?.TelSysID,
                            partition,
                            securityGroup: secGroupValueRef.current,
                            search: searchTerm,
                            users: devices.map(({ isAgent, original }: DeviceSelectItem): SelfProvisioningUser => {
                                const isTelSysTeams: boolean = telsys?.TelSysType === TelSysTypes.MS_TEAMS
                                const isTelSysWebex: boolean = telsys?.TelSysType === TelSysTypes.WEBEX_CALLING

                                let userType: string = UserTypes.NONE

                                if (!isTelSysTeams) {
                                    userType = isAgent ? UserTypes.CALL_CENTRE_AGENT : UserTypes.ESSENTIALS
                                }

                                return {
                                    email:
                                        (isTelSysTeams ? original?.TelSysUsername ?? EMPTY_STRING : EMPTY_STRING) ||
                                        (isTelSysWebex ? original?.PrimaryEmail ?? EMPTY_STRING : EMPTY_STRING),
                                    userType,
                                    welcomeEmail: userDefaults.default.welcomeEmail,
                                    cxiLicence: userDefaults.default.cxiLicence ?? CxIntegrationLicence.NONE,
                                    cxiDataEntitlement: false,
                                    useEmailAsUsername: false
                                }
                            })
                        })
                    }
                })
                .catch((e: Error): void => {
                    displayError(e)
                    setLoadingText(undefined)
                })

            updateLicenceSummary()
        }
    }

    const telSysMessage: Nullable<MessageResponseTuple> = useMemo((): Nullable<MessageResponseTuple> => {
        if (isVarNotEmpty(currentUserCount)) {
            if (currentUserCount === 0 && !ArrayUtils.isEmpty(currentTelSys?.AuthUserMissingRoles)) {
                return ['error', language.get(UsersByExtensionLanguage.Telsys_MissingRoles_Day0)]
            }

            if (currentUserCount > 0 && !ArrayUtils.isEmpty(currentTelSys?.AuthUserMissingRoles)) {
                return ['warning', language.get(UsersByExtensionLanguage.Telsys_MissingRoles_Day1)]
            }
        }

        return undefined
    }, [currentTelSys, currentUserCount])

    useEffect((): void => {
        secGroupValueRef.current = secGroupValue
    }, [secGroupValue])

    useEffect((): void => {
        if (userInfo && !hasLoaded.current) {
            hasLoaded.current = true
            setIsLoadingTelServers(true)
            setValue(PARTITION_FILTER_NAME, undefined)
            getTelServersAsSelectOptions(
                userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID,
                true,
                true,
                ReportingAccessLevels.ENTERPRISE
            )
                .then((telServers: SelectOptions<number>): void => {
                    if (isMounted.current) {
                        setIsLoadingTelServers(false)
                        setAvailablePartitions(undefined)
                        setValue(PARTITION_FILTER_NAME, undefined)

                        if (
                            userInfo?.Scope?.TelSysID &&
                            userInfo.Scope.TelSysID.length > 0 &&
                            userInfo.Scope.TelSysID[0] === ALL_VALUE &&
                            telServers.length > 0
                        ) {
                            setValue(TELSYS_FILTER_NAME, telServers[0].value)
                        } else {
                            setValue(TELSYS_FILTER_NAME, userInfo.Scope.TelSysID[0])
                        }

                        handleUnknownForAvailables(telServers, setAvailableTelServers, telSysValue)
                    }
                })
                .catch(displayError)
        }
    }, [userInfo])

    useEffect((): void => {
        if (telSysValue && telSysValue !== ALL_VALUE) {
            setIsPartitionChangeRequired(true)
            setIsLoadingPartitions(true)
            setAvailableSecurityGroups([])

            if (formContext?.resetDirtyFields) {
                formContext?.resetDirtyFields()
            }

            const telSysFields: string[] = [
                HISTORIC_ONLY,
                INTEGRATION_MODE,
                INTEGRATION_TYPE,
                ADVANCED_FIELDS.MAXIMUM_ALLOWED_USERS_MONITORED,
                AUTH_USER_MISSING_ROLES
            ]

            getTelephonyServer(telSysValue, ...telSysFields)
                .then((newTelSys: Nullable<IndividualTelItem>): void => {
                    setCurrentTelSys(newTelSys)

                    getPartitionsForTelServer(telSysValue)
                        .then(
                            (partitions: SelectOptions<number>): SelectOptions<number> =>
                                partitions.filter(
                                    (partition: SelectOption<number>): boolean =>
                                        partition.label !== COMMON_DEVICES_PARTITION_NAME
                                )
                        )
                        .then((partitions: SelectOptions<number>): void => {
                            if (isMounted.current) {
                                setIsLoadingPartitions(false)
                                setValue(PARTITION_FILTER_NAME, undefined)

                                if (partitions.length > 0) {
                                    const newPartitionValue: number = partitions.find((): boolean => true)?.value ?? 0
                                    setValue(PARTITION_FILTER_NAME, newPartitionValue)

                                    handleUnknownForAvailables(partitions, setAvailablePartitions, newPartitionValue)

                                    if (
                                        newTelSys &&
                                        newTelSys.TelSysType === TelSysTypes.MS_TEAMS &&
                                        isVarNotEmpty(newPartitionValue)
                                    ) {
                                        getDevicesFromPartition(telSysValue, newPartitionValue, false, [
                                            DeviceTypeIdString.GroupSecurityGroup
                                        ])
                                            .then(([secGroups]: [DeviceSelectItem[], Definable<number>]): void => {
                                                setAvailableSecurityGroups([
                                                    {
                                                        label: language.get(
                                                            UsersByExtensionLanguage.UsersByExtensions_AllSecGroups
                                                        ),
                                                        value: ALL_VALUE_STRING_DEFAULT
                                                    },
                                                    ...secGroups
                                                ])
                                            })
                                            .catch(displayError)
                                    }
                                }

                                setTimeout((): void => {
                                    if (isMounted.current) {
                                        setIsPartitionChangeRequired(false)
                                    }
                                })
                            }
                        })
                        .catch(displayError)
                })
                .catch(displayError)

            getCMEIDFromTelSys(telSysValue)
                .then((cme: Definable<string>): void => {
                    if (IsVarEmpty(cme)) {
                        if (isMounted.current) {
                            setCmeID(cme ?? DEFAULT_CME_ID)
                        }
                        return
                    }

                    getCapabilitiesFromCME(cme)
                        .then((capabiltiesFromCME: Definable<number>): void => {
                            if (isMounted.current) {
                                setCapabilities(capabiltiesFromCME)
                                setCmeID(cme)
                            }
                        })
                        .catch(displayError)
                })
                .catch(displayError)
        }
    }, [telSysValue])

    useEffect((): void => {
        getIsPartitionChangeRequired().then((partitionChangeReq: boolean): void =>
            getDevices(currentTelSys, partitionValue, partitionChangeReq, undefined, undefined)
        )
    }, [userInfo, currentTelSys, partitionValue, isPartitionChangeRequired, secGroupValue])

    useEffect((): void => {
        if (telSysValue && telSysValue !== ALL_VALUE && currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS) {
            countLicencedUsersForTelSysPartition(telSysValue).then((userCount: Nullable<number>): void => {
                if (isMounted.current) {
                    setCurrentUserCount(userCount ?? 0)
                }
            })
        } else {
            setCurrentUserCount(0)
        }
    }, [telSysValue, JSON.stringify(availableExtensions)])

    return (
        <div className={`users-by-extension ${isTeams ? 'is-teams' : ''} ${isCxiOnly ? 'is-cxi-only' : ''}`}>
            <PaginatedList
                horizontalScrollGroup
                entries={availableExtensions}
                totalEntries={totalExtensionCount}
                changePage={(currentPage?: number): void => {
                    if (isMounted.current) {
                        getIsPartitionChangeRequired().then((partitionChangeReq: boolean): void =>
                            getDevices(
                                currentTelSys,
                                partitionValue,
                                partitionChangeReq,
                                (currentPage ?? 0) * 10,
                                currentSearchTerm
                            )
                        )
                    }
                }}
                searchFunction={(newSearch: Nullable<string>): void => {
                    if (isMounted.current) {
                        setCurrentSearchTerm(newSearch)

                        getIsPartitionChangeRequired().then((partitionChangeReq: boolean): void =>
                            getDevices(currentTelSys, partitionValue, partitionChangeReq, 0, newSearch)
                        )
                    }
                }}
                entriesPerPage={10}
                hasForm
                errors={extensionErrors}
                isLoading={isStringNotEmpty(loadingText)}
                loadingTitle={loadingText}
                updateDependencies={[licenceSummary]}
                entryDisplayAsyncMapper={(extension: DeviceSelectItem, index: number): Promise<ReactElement> =>
                    getExtensionErrors().then(
                        (errors: Definable<string>[]): ReactElement => (
                            <UserByExtensionEntry
                                key={`${JSON.stringify(extension)}-${index}`}
                                extension={extension}
                                cmeID={cmeID}
                                updateDeviceUsers={updateDeviceUsers}
                                updateExtensionErrors={updateExtensionErrors}
                                extensionError={errors[index]}
                                telsysType={currentTelSys?.TelSysType ?? TelSysTypes.UNKNOWN}
                                callControlOptions={currentTelSys?.CallControlOptions}
                                licenceSummary={licenceSummary}
                                updateLicenceSummary={updateLicenceSummary}
                                shouldShowReportingTypeColumn={isReportingTypeColumnVisible}
                                shouldShowCXSColumn={showCxsSubscriptionColumn}
                                isCXScoreFeatureEnabled={isCXScoreFeatureEnabled}
                                shouldShowCXIntegrationColumn={isCxiEnabled}
                                showCxiUserField={cxiEntitlementVisibility.showCxiUser}
                                showCxiDataField={cxiEntitlementVisibility.showCxiData}
                                shouldShowWelcomeMailColumn={!isCxiOnly}
                                availableTelServers={availableTelServers}
                            />
                        )
                    )
                }
                entryHeaders={[
                    { header: language.get(UsersByExtensionLanguage.UsersByExtensions_Header_Users) },
                    ...(isTeams &&
                    availableExtensions.some((device: DeviceSelectItem): boolean =>
                        isVarNotEmpty(device.original?.LicenceType)
                    )
                        ? [
                              {
                                  header: language.get(UsersByExtensionLanguage.UsersByExtensions_Header_MS_Licences),
                                  classes: 'ms-licence'
                              }
                          ]
                        : []),
                    {
                        header: (
                            <Fragment key='email-address-header'>
                                {language.get(UsersByExtensionLanguage.UsersByExtensions_UserFields_Email)}
                                <HelpTag
                                    tag={Tags.EASY_PROVISIONING}
                                    helpContentTitle={HelpTagTitles.EASY_PROVISIONING_EMAIL}
                                />
                            </Fragment>
                        )
                    },
                    ...(isCxiOnly
                        ? []
                        : [
                              {
                                  header: (
                                      <span
                                          title={language.get(
                                              UsersByExtensionLanguage.UsersByExtensions_UserFields_Welcome_Mail
                                          )}
                                      >
                                          <IconButton
                                              iconName={IconName.EMAIL}
                                              type={IconButtonType.BUTTON}
                                              onClick={toggleWelcomeAll}
                                          />
                                      </span>
                                  ),
                                  classes: 'pagination-entry-header-mail'
                              }
                          ]),
                    ...(isReportingTypeColumnVisible
                        ? [
                              {
                                  header: (
                                      <Fragment key='subscription-type-header'>
                                          {language.get(UsersByExtensionLanguage.UsersByExtensions_UserFields_Type)}
                                          <HelpTag
                                              tag={Tags.EASY_PROVISIONING}
                                              helpContentTitle={HelpTagTitles.EASY_PROVISIONING_SUB_TYPE}
                                          />
                                      </Fragment>
                                  )
                              }
                          ]
                        : []),
                    ...(showCxsSubscriptionColumn
                        ? [
                              {
                                  header: language.get(
                                      UsersByExtensionLanguage.UsersByExtensions_CXS_Subscription_Header
                                  )
                              }
                          ]
                        : []),
                    ...(cxiEntitlementVisibility.showCxiUser
                        ? [
                              {
                                  header: (
                                      <Fragment key='crm-integration-header'>
                                          {language.get(UsersByExtensionLanguage.UsersByExtensions_UserFields_CXIUser)}
                                          <HelpTag
                                              tag={Tags.CRM_INTEGRATION}
                                              helpContentTitle={HelpTagTitles.CRM_INTEGRATION}
                                          />
                                      </Fragment>
                                  ),
                                  classes:
                                      currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS ||
                                      currentTelSys?.TelSysType === TelSysTypes.WEBEX_CALLING
                                          ? 'crm-integration-licence-single-option'
                                          : ''
                              }
                          ]
                        : []),
                    ...(cxiEntitlementVisibility.showCxiData
                        ? [
                              {
                                  header: language.get(UsersByExtensionLanguage.UsersByExtensions_UserFields_CXIData),
                                  classes: 'crm-integration-licence-single-option'
                              }
                          ]
                        : [])
                ]}
                subHeaderContent={
                    <>
                        {!ArrayUtils.isEmpty(telSysMessage) && telSysMessage?.length === 2 ? (
                            <StatusMessage type={telSysMessage?.[0]} message={telSysMessage?.[1] ?? EMPTY_STRING} />
                        ) : (
                            <></>
                        )}
                        <div className='ube-subheader-actions'>
                            {currentTelSys?.TelSysType === TelSysTypes.MS_TEAMS ? (
                                <>
                                    {isReportingTypeColumnVisible && (
                                        <AddAllUsers
                                            addAllUsers={addAllUsers}
                                            telSysType={currentTelSys?.TelSysType}
                                            availableExtensions={availableExtensions.length}
                                            isAdminProvisioningUser={isAdminProvisioningUser}
                                        />
                                    )}
                                    <LicenceSummary
                                        licenceSummary={licenceSummary}
                                        telSysType={currentTelSys?.TelSysType}
                                        shouldIncludeCXIUser={cxiEntitlementVisibility.showCxiUser}
                                        shouldIncludeCXIData={cxiEntitlementVisibility.showCxiData}
                                    />
                                </>
                            ) : (
                                <></>
                            )}
                            {cxiEntitlementVisibility.showCxiUser && (
                                <ProvisionAllCxi
                                    cxIntegrationLicenseType={cxIntegrationLicenceType}
                                    provisionAllUsers={provisionAllUnlicensedDevicesWithCXIntegration}
                                />
                            )}
                        </div>
                    </>
                }
                headerContent={
                    availableTelServers && availableTelServers.length > 0 ? (
                        <div className='ube-filters'>
                            <div className='ubef-filter-title'>
                                {language.get(UsersByExtensionLanguage.UsersByExtensions_Filters)}
                            </div>
                            <div className='ubef-filter-options'>
                                <ComboBox
                                    label=''
                                    name={TELSYS_FILTER_NAME}
                                    defaultOption={availableTelServers[0]}
                                    options={availableTelServers.slice(1)}
                                    disabled={
                                        isLoadingTelServers ||
                                        (userInfo?.Scope?.TelSysID?.length === 1 &&
                                            userInfo?.Scope?.TelSysID[0] !== ALL_VALUE)
                                    }
                                    onChange={(): void => setValue(PARTITION_FILTER_NAME, undefined)}
                                />
                                {availablePartitions && availablePartitions.length > 0 ? (
                                    <ComboBox
                                        label=''
                                        name={PARTITION_FILTER_NAME}
                                        defaultOption={availablePartitions[0]}
                                        options={availablePartitions.slice(1)}
                                        disabled={
                                            isLoadingPartitions ||
                                            (userInfo?.Scope?.PartIDInTelSys?.length === 1 &&
                                                userInfo?.Scope?.PartIDInTelSys[0] !== ALL_VALUE)
                                        }
                                    />
                                ) : (
                                    <></>
                                )}
                                {availableSecurityGroups && availableSecurityGroups.length > 0 ? (
                                    <ComboBox
                                        label={EMPTY_STRING}
                                        name={SECURITY_GROUP_FILTER_NAME}
                                        defaultOption={availableSecurityGroups[0]}
                                        options={availableSecurityGroups.slice(1)}
                                        disabled={
                                            isLoadingPartitions ||
                                            (userInfo?.Scope?.PartIDInTelSys?.length === 1 &&
                                                userInfo?.Scope?.PartIDInTelSys[0] !== ALL_VALUE)
                                        }
                                    />
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                    ) : undefined
                }
            />
        </div>
    )
}
